import { AuthInvalidSessionError, AuthRefreshAbortError, AuthRefreshFlowError } from './errors.js';
import { locks } from './locks.js';
import { createLogger } from './private/logger.js';
import { createStore } from './private/store.js';
import { isStorageWithSubscription, } from './types.js';
import { accessTokenDataIsValid, accessTokenIsExpired, coerceToError, createAccessTokenData, retryOnRejection, } from './utils.js';
/**
 * Schedules the callback to be executed in the shared global sequential queue.
 *
 * This relies on the Web Locks API to ensure that the tasks are executed one at a time even between multiple browser tabs.
 * Note that, according to the Web Locks spec, the tasks may not be executed in the order in which they were enqueued.
 *
 * NB! Be careful not to nest `enqueueTask` calls to avoid deadlocks.
 */
function enqueueTask(fn) {
    return locks.request('wolt-auth-refresh', fn);
}
/** Creates an authenticator object instance. */
export function createAuthenticator(configuration) {
    const { storage, expirationOffset = 60 * 1000, authService, maxRetries = 10, userType = ['wolt'], } = configuration;
    const logger = createLogger(configuration.logger);
    // This assignment is needed solely to satisfy Typescript's generic inference
    const loginService = authService;
    /** Ensures that the authenticator receives only valid access token data upon initialization. */
    function sanitizeAccessTokenData(accessTokenData) {
        if (!accessTokenData)
            return undefined;
        if (!accessTokenDataIsValid(accessTokenData)) {
            storage.clear();
            console.error('Attempted to read auth data from persistent storage but stored data is corrupted');
            return undefined;
        }
        return accessTokenData;
    }
    async function hasValidAuthDataStored() {
        const authData = await storage.read();
        return authData && accessTokenDataIsValid(authData);
    }
    function retryOnFailure(fn) {
        return retryOnRejection(fn, { maxRetries });
    }
    function createPrototype({ dispatch, getState }) {
        async function getAccessToken({ lock = true } = {}) {
            logger.logMethod('getAccessToken');
            if (getState().status !== 'logged-in') {
                // Note: we must throw here, since getAccessToken() must always resolve with a token,
                // and we're already logged out.
                // This however isn't expected and indicates a bug on the consuming side.
                throw new AuthRefreshFlowError("Attempted to obtain access token but user isn't logged in");
            }
            async function task() {
                const authData = await storage.read();
                if (getState().status !== 'logged-in' || !authData) {
                    await dispatch({ type: 'no_auth_logout' });
                    // Note: we must throw here, since getAccessToken() must always resolve with a token,
                    // and we're already logged out.
                    // This however is expected if the user logged out in another tab while this tab was awaiting getAccessToken().
                    // So, the error here can be safely discarded on the consuming side - it has the same semantics as e.g. AbortError.
                    throw new AuthRefreshAbortError('Already logged out when trying to refresh token');
                }
                let accessToken = authData.accessToken;
                if (accessTokenIsExpired(authData, { expirationOffset })) {
                    const { refreshToken } = authData;
                    accessToken = await refreshAccessToken(refreshToken);
                }
                return accessToken;
            }
            return lock ? enqueueTask(task) : task();
        }
        /**
         * Manually refreshes the access token and stores and returns the new one.
         *
         * Note that it must be always enqueued with `enqueueTask` to avoid multi-tab race conditions. */
        const refreshAccessToken = async (refreshToken) => {
            const refreshGrant = { grantType: 'refresh_token' };
            if (refreshToken) {
                refreshGrant.refreshToken = refreshToken;
            }
            let accessTokenData;
            try {
                accessTokenData = await retryOnFailure(() => loginService.callAccessTokenApi(refreshGrant));
            }
            catch (error) {
                console.error('@creditornot/wolt-auth failed to refresh access token');
                await dispatch({ type: 'refresh_failure' });
                throw error;
            }
            if (accessTokenData instanceof Error) {
                if ('authorize' in authService) {
                    const authorizeStatus = await authService.authorize();
                    if (authorizeStatus === 'logged-in') {
                        return getAccessToken({ lock: false });
                    }
                }
                // This signifies a forced logout condition - an invalid refresh token was used.
                // This is expected if the refresh token got delisted, e.g. because the user hasn't logged in in a while,
                // so we throw AuthRefreshAbortError, which can be safely discarded on the consuming side
                await dispatch({ type: 'forced_logout' });
                throw new AuthRefreshAbortError("Can't perform refresh token flow. Session has expired");
            }
            await dispatch({
                type: 'refresh_success',
                accessTokenData: createAccessTokenData(accessTokenData),
            });
            return accessTokenData.access_token;
        };
        async function logout() {
            logger.logMethod('logout');
            if (getState().status === 'logged-out') {
                console.warn('logout() was called while already logged out');
                return;
            }
            await enqueueTask(async () => {
                if (getState().status === 'logged-out') {
                    // Note: we don't issue a warning here since this is expected if
                    // the user has logged out in another tab by the time this task executes in this tab
                    return;
                }
                const authData = await storage.read();
                if (!authData) {
                    // This is unexpected and the logout can't be performed, so we throw here
                    throw new AuthInvalidSessionError("Couldn't perform logout as no auth data is stored in persistent storage");
                }
                try {
                    // The difference between WAuth2 and OAuth2 logout procedures lies in their implementation:
                    // WAuth2 requires an API call for logout, whereas OAuth2 utilizes a redirect-based process.
                    // Check whether the current authentication service supports the logout API call.
                    if ('callLogoutApi' in authService) {
                        await retryOnFailure(() => authService.callLogoutApi(authData));
                    }
                    await dispatch({ type: 'logout' });
                }
                catch (err) {
                    console.error(err);
                    throw err;
                }
            });
        }
        return {
            async login(grant, { replace = false, lock = true } = {}) {
                logger.logMethod('login');
                if (getState().status === 'logging-in') {
                    console.warn('login() was called while already logging in');
                    return;
                }
                if (!replace && getState().status === 'logged-in') {
                    console.warn('login() was called while already logged in');
                    return;
                }
                if (!replace) {
                    await dispatch({ type: 'login_request' });
                }
                async function task() {
                    if (replace) {
                        if ('callLogoutApi' in authService) {
                            const authData = await storage.read();
                            if (authData) {
                                await retryOnFailure(() => authService.callLogoutApi(authData));
                            }
                        }
                    }
                    // Another tab may have put auth data in the storage already,
                    // but this tab is still yet to react to the storage change event and update its state.
                    // Avoid making a login request in this case.
                    else if (getState().status === 'logged-in' || (await hasValidAuthDataStored())) {
                        // Note: we don't issue a warning here since this is expected if the user has logged in
                        // in another tab by the time this task is executed.
                        return;
                    }
                    try {
                        const accessTokenResponse = await retryOnFailure(() => loginService.callAccessTokenApi(grant));
                        if (accessTokenResponse instanceof Error) {
                            // This means that the service doesn't recognize the credentials according to the specification.
                            await dispatch({
                                type: 'login_error',
                                error: accessTokenResponse,
                            });
                            throw accessTokenResponse;
                        }
                        const accessTokenData = createAccessTokenData(accessTokenResponse);
                        await dispatch({ type: 'login_success', accessTokenData });
                    }
                    catch (err) {
                        // This is the general service failure case such as e.g. 500 or network error
                        await dispatch({ type: 'login_error', error: coerceToError(err) });
                        throw err;
                    }
                }
                return lock ? enqueueTask(task) : task();
            },
            logout,
            getAccessToken,
            async refreshUser({ lock = true } = {}) {
                logger.logMethod('refreshUser');
                if (getState().status !== 'logged-in') {
                    console.warn('refreshUser() was called while logged out');
                    return;
                }
                async function task() {
                    const authData = await storage.read();
                    if (getState().status !== 'logged-in' || !authData) {
                        // Note: we don't issue a warning here since this is expected if the user has logged out in another tab
                        // by the time this task executes in this tab.
                        // In this case, this tab is no longer interested in refreshing the user.
                        return;
                    }
                    await refreshAccessToken(authData.refreshToken);
                }
                return lock ? enqueueTask(task) : task();
            },
        };
    }
    function initialize(initialAccessTokenData) {
        const store = createStore({
            initialAccessTokenData: sanitizeAccessTokenData(initialAccessTokenData),
            storage,
            logger,
            userType,
        });
        const { getState, subscribe, unsubscribe } = store;
        let { unsubscribeAll } = store;
        // For storages that support subscriptions, install a listener for external changes
        if (isStorageWithSubscription(storage)) {
            const storageUnsubscribe = storage.subscribe(() => {
                enqueueTask(async () => {
                    const accessTokenData = await storage.read();
                    const sessionIsValid = accessTokenData && accessTokenDataIsValid(accessTokenData);
                    if (sessionIsValid) {
                        console.info('@creditornot/wolt-auth detected a change in session in another tab. Syncing...');
                        // Update the state if we detect that the access token data was changed externally,
                        // e.g. by logging in or refreshing the access token in another tab.
                        await store.dispatch({ type: 'storage_sync', accessTokenData });
                    }
                    else if (store.getState().status !== 'logged-out') {
                        // Log the user out if we detect that the access token data was removed externally,
                        // e.g. by clearing the user data or logging-out in another tab.
                        // If we don't do this, the next getAccessToken() call will reject as the tokens won't be found.
                        console.info('@creditornot/wolt-auth detected that access token data was removed from storage. Logging out...');
                        await store.dispatch({ type: 'logout_sync' });
                    }
                });
            });
            unsubscribeAll = () => {
                store.unsubscribeAll();
                storageUnsubscribe();
            };
        }
        return {
            ...createPrototype(store),
            getState,
            subscribe,
            unsubscribe,
            unsubscribeAll,
        };
    }
    const initialAccessTokenData = storage.read();
    if (initialAccessTokenData instanceof Promise) {
        return initialAccessTokenData
            .catch((error) => {
            console.error(error);
            return undefined;
        })
            .then(initialize);
    }
    return initialize(initialAccessTokenData);
}
